import { FlaskConical, Pause, Play, RefreshCw } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import {
  Link,
  useNavigationType,
  useParams,
  useSearchParams,
} from "react-router";

import { queryKeys, useProjectOverviewQuery } from "@/api/hooks";
import {
  classifyRunReadFailure,
  usePreviewRunQuery,
} from "@/api/previewInsights";
import { QueryPendingState } from "@/api/QueryPendingState";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import {
  RUN_EVENT_PARAM,
  RUN_TABS,
  RUN_TAB_LABELS,
  RUN_TAB_PARAM,
  readEventRefParam,
  readRunTabParam,
  type RunTabId,
} from "@/lib/previewDecisions";
import {
  CAPTURE_STATE_LABELS,
  IDENTITY_AXES,
  IDENTITY_LABELS,
  OMISSION_REASON_LABELS,
  RUN_POLL_INTERVAL_MS,
  SYNTHETIC_PREVIEW_DISCLAIMER,
  VIEW_LABELS,
  VIEW_TONES,
  candidateDisplayVersion,
  canStillChange,
  completenessOf,
  derivePreviewRunView,
  describeTargetRevision,
  describeView,
  isAttested,
  lastUpdateOf,
  type PreviewRunDispatch,
  type PreviewRunView,
  type RunIdentity,
} from "@/lib/previewInsights";
import { closestDirection, horizontalStep } from "@/lib/textDirection";
import { PageHeader } from "@/shell/PageHeader";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { PreviewRunCompare } from "@/ui/preview/PreviewRunCompare";
import { PreviewRunConversation } from "@/ui/preview/PreviewRunConversation";
import { PreviewRunDecisions } from "@/ui/preview/PreviewRunDecisions";
import { PreviewRunReadFailure } from "@/ui/preview/PreviewRunReadFailure";
import { ReplayFromTurnPanel } from "@/ui/preview/ReplayFromTurnPanel";
import { DeniedState, StaleDataNotice } from "@/ui/states";

import { PreviewRunPromptInspector } from "./PreviewRunPromptInspector";
import { Fact, Identifier } from "./previewRunParts";

/**
 * Preview Insights — the native run page (#1098, approved design §10).
 *
 * Reached from the preview dialog's `Open Preview Insights` action, or by a
 * saved link. The address carries the Project id and the opaque
 * `previewRunRef` the Editor minted at dispatch — and NOTHING about the run:
 * no patient, no candidate, no preview target, no Liv or Brain session
 * (ADR 0001; ADR 0013 N6). `src/invariants/routes.test.ts` holds the route
 * table to that, and `previewEgress.test.tsx` holds the request bytes to it.
 *
 * ## What this page is, and deliberately is not yet
 *
 * It is the SHELL: the fixed synthetic-preview disclaimer, the run's typed
 * state, its completeness and last update, the runtime and candidate
 * evidence the producer attested, a polling lifecycle over the compact
 * envelope, and the tabs the design names. The Prompt Inspector tab
 * lists every prompt call of the run, paged and narrowed by the server
 * (#1100, `PreviewRunPromptInspector.tsx`), and loads ONE selected call's
 * bodies on demand (#1099, `PreviewRunPromptCall.tsx`) — never by this page's
 * timer. The Flow & Decisions tab (#1102) reads and polls the compact event
 * page once opened. The `Replay from a turn` panel (#1105) reads compact
 * `turn` events — bodies by reference, never text — to list the boundaries a
 * replay may name. The Conversation tab renders the run's `turn` events: the
 * runtime's current position, the Anxiety view, a patient-simulator panel for
 * runs a simulator drove, and each turn's text — read through the one
 * body-bearing event-detail read, only once the clinician asks for it. No
 * comparison is fetched here.
 *
 * ## The address names the tab, and may name one event
 *
 * `?tab=` selects a tab and `?event=` names the event that tab should open on
 * — the two parameters an evidence link carries besides the run page's own
 * address. Both are read through `lib/previewDecisions.ts`: a tab outside the
 * three is the default tab, and a value that is not shaped like the producer's
 * opaque event reference is dropped before it can reach a query key or a
 * request. Neither parameter names anything the run is about (ADR 0013 N6).
 *
 * ## Truthful states
 *
 * Every state below is one the response actually stated, in the vocabulary
 * `lib/previewInsights.ts` reads it into. The page never infers a run ended,
 * never renders an absent capture as an empty conversation, and never lets a
 * partial trace read as a complete one (ADR 0013 N7). Failures are kept
 * apart the way the contract keeps them apart: a refusal (403), the ONE
 * generic not-found (404 — the browser cannot and must not say why), and the
 * two 503s with different recoveries.
 *
 * ## Polling never touches the conversation
 *
 * The timer re-reads the envelope and nothing else; pausing it, or leaving the
 * page, changes what this view shows and nothing in Liv or Brain. A hidden
 * tab polls nothing (TanStack's `refetchIntervalInBackground` default), and a
 * run that cannot change is not asked again until the clinician says so.
 */
export function ProjectPreviewRunRoute({
  pollIntervalMs = RUN_POLL_INTERVAL_MS,
}: {
  /** The re-read cadence while the run can still change. Tests shorten it. */
  pollIntervalMs?: number;
} = {}) {
  const { projectId = "", previewRunRef = "" } = useParams();
  const inspect = useCapability("preview.inspect");
  // The project's name for the trail, and its CURRENT target revision so the
  // evidence card can say when the project moved on since the dispatch.
  // Deliberately NOT the candidate listing: the candidate this run served is
  // named by the service's own dispatch record on the run read, never located
  // in the browser by content hash (#1098 review, findings 1 and 4).
  const overview = useProjectOverviewQuery(projectId, {
    alwaysRefetchOnMount: true,
  });
  const [paused, setPaused] = useState(false);
  const run = usePreviewRunQuery(projectId, previewRunRef, {
    enabled: inspect.allowed,
    paused,
    pollIntervalMs,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const tab: RunTabId =
    readRunTabParam(searchParams.get(RUN_TAB_PARAM)) ?? "conversation";
  const focusEventRef = readEventRefParam(searchParams.get(RUN_EVENT_PARAM));
  const selectTab = (next: RunTabId) => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set(RUN_TAB_PARAM, next);
        // The event an evidence link named belongs to the tab it opened; a
        // tab the clinician chooses opens on nothing in particular.
        params.delete(RUN_EVENT_PARAM);
        return params;
      },
      { replace: true },
    );
  };

  // FOCUS LANDS ON THE HEADING after an in-app navigation. The control that
  // brought a keyboard user here — the dialog's link — is gone with the page
  // it was on, and the dialog's own focus return has no opener to restore to,
  // so without this focus falls to `body` and a screen reader announces
  // nothing. A fresh load or history traversal (`POP`) is left alone: the
  // browser already put focus where it belongs for those.
  const navigationType = useNavigationType();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (navigationType === "PUSH") {
      headingRef.current?.focus();
    }
  }, [navigationType, previewRunRef]);

  const projectName = overview.data?.project.name ?? null;
  const base = `/projects/${encodeURIComponent(projectId)}`;

  const frame = (body: React.ReactNode) => (
    <>
      <PageHeader
        breadcrumbs={[
          { label: "Projects", to: "/projects" },
          { label: projectName ?? "Project", to: base },
          { label: "Preview Insights" },
        ]}
        title="Preview Insights"
        titleRef={headingRef}
        description={
          <>
            How Liv ran a synthetic preview of{" "}
            {projectName ? (
              // `dir="auto"`: a Hebrew project name chooses its own direction
              // inside an English sentence, whichever way the document runs.
              <span dir="auto" className="font-medium text-foreground">
                {projectName}
              </span>
            ) : (
              "this project"
            )}
            : status, completeness and runtime identity, read from the recorded
            preview target.
          </>
        }
        actions={
          <Button variant="outline" asChild>
            <Link to={base}>Back to project</Link>
          </Button>
        }
      />
      {/*
        THE FIXED DISCLAIMER, on every state of this page including the ones
        that never see a response (approved design §10.2: "The page header
        always shows"). A plain paragraph rather than a live region: it never
        changes, so there is nothing for assistive technology to be told
        about after the page is read once.
      */}
      <p className="mb-6 flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm font-medium text-foreground">
        <FlaskConical aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span>{SYNTHETIC_PREVIEW_DISCLAIMER}</span>
      </p>
      {body}
    </>
  );

  // CAPABILITY-NEGATIVE, decided before any request. The browser mirrors the
  // server's grant and never widens it; it also does not ask the server a
  // question it already knows the answer to, every five seconds.
  if (!inspect.allowed) {
    return frame(
      <DeniedState
        title="Preview Insights is not available to your account"
        description={`${inspect.reason ?? ""} Inspecting a preview run needs the "preview.inspect" permission, which Project Editors, Clinical Managers and Administrators hold. Ask a workspace administrator if you need it.`}
      />,
    );
  }

  // NOTHING FROM A PREVIOUS VISIT IS SHOWN AS THIS ONE'S ANSWER — the
  // `isFetchedAfterMount` gate `ProjectContentSources` documents.
  if (run.isPending || !run.isFetchedAfterMount) {
    return frame(
      <QueryPendingState
        isPaused={run.isPaused}
        failureReason={run.failureReason}
        label="Loading preview run"
        errorTitle="This preview run could not be read"
        rows={3}
        queryKey={queryKeys.previewRun(projectId, previewRunRef)}
      />,
    );
  }

  // A transient failure keeps the last envelope on screen under a stale
  // notice; a terminal one — refusal, not-found, expired sign-in — drops it,
  // because access to the run has definitively changed.
  if (run.isError && (!run.data || isTerminalQueryError(run.error))) {
    return frame(
      <PreviewRunReadFailure
        failure={classifyRunReadFailure(run.error)}
        error={run.error}
        onRetry={() => void run.refetch()}
      />,
    );
  }

  const view = derivePreviewRunView(run.data);
  const polling = canStillChange(view);

  return frame(
    <div className="space-y-6">
      <StaleDataNotice
        updatedAt={run.dataUpdatedAt}
        onRetry={() => void run.refetch()}
        visible={run.isError}
      />

      <RunStatusCard
        view={view}
        polling={polling}
        paused={paused}
        pollIntervalMs={pollIntervalMs}
        refreshing={run.isFetching}
        onTogglePause={() => setPaused((current) => !current)}
        onRefresh={() => void run.refetch()}
      />

      <RunEvidenceCard
        dispatch={run.data.dispatch}
        currentTargetRevision={
          overview.isSuccess && overview.isFetchedAfterMount
            ? overview.data.project.previewTargetRevision
            : null
        }
        identity={view.summary?.identity ?? null}
      />

      {/*
        Replay from a turn (#1105): the source run's user-turn boundaries and
        the command dialog. KEYED BY THE LOCATOR so that opening the replay's
        destination run — the same route component under a new parameter —
        starts from a clean panel rather than one still holding the source's
        boundary and dialog state.
      */}
      <ReplayFromTurnPanel
        key={previewRunRef}
        projectId={projectId}
        previewRunRef={previewRunRef}
        view={view}
        dispatch={run.data.dispatch}
        onRefreshRun={() => void run.refetch()}
        pollIntervalMs={pollIntervalMs}
      />

      <RunTabs
        view={view}
        tab={tab}
        onSelect={selectTab}
        focusEventRef={focusEventRef}
        projectId={projectId}
        previewRunRef={previewRunRef}
        paused={paused}
        pollIntervalMs={pollIntervalMs}
      />
    </div>,
  );
}

function RunStatusCard({
  view,
  polling,
  paused,
  pollIntervalMs,
  refreshing,
  onTogglePause,
  onRefresh,
}: {
  view: PreviewRunView;
  polling: boolean;
  paused: boolean;
  pollIntervalMs: number;
  refreshing: boolean;
  onTogglePause: () => void;
  onRefresh: () => void;
}) {
  const lastUpdate = lastUpdateOf(view);
  const completeness = view.summary ? completenessOf(view.summary) : null;

  const pollingSentence = !polling
    ? "Automatic updates stopped: this run can no longer change on its own."
    : paused
      ? "Automatic updates paused. The preview conversation is not affected."
      : `Checks for updates every ${describeInterval(pollIntervalMs)} while this page is visible.`;

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle>Run status</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {polling ? (
            <Button
              variant="outline"
              size="sm"
              aria-pressed={paused}
              onClick={onTogglePause}
            >
              {paused ? (
                <Play aria-hidden="true" className="h-4 w-4" />
              ) : (
                <Pause aria-hidden="true" className="h-4 w-4" />
              )}
              {paused ? "Resume updates" : "Pause updates"}
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Refresh now
          </Button>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        {/*
          The state and its sentence in ONE polite live region: they change
          together and only when the run's state changes. The counts and the
          timestamp below are deliberately outside it — a last-update time
          that moves on every poll would otherwise be announced every poll.
        */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="flex flex-wrap items-start gap-3"
        >
          <Badge tone={VIEW_TONES[view.kind]}>{VIEW_LABELS[view.kind]}</Badge>
          <p className="min-w-0 flex-1 text-sm text-foreground">
            {describeView(view)}
          </p>
        </div>

        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Last update">
            {lastUpdate ? (
              <time dateTime={lastUpdate} dir="ltr">
                {formatDateTime(lastUpdate)}
              </time>
            ) : (
              "Not reported"
            )}
          </Fact>
          <Fact label="Capture">
            {CAPTURE_STATE_LABELS[view.run.captureState]}
          </Fact>
          <Fact label="Completeness">
            {completeness ? (
              <CompletenessSentence
                completeThroughSequence={completeness.completeThroughSequence}
                lastSequence={completeness.lastSequence}
                missingRangeCount={completeness.missingRangeCount}
                shortfallOmissionCount={completeness.shortfallOmissionCount}
                restrictedOmissionCount={completeness.restrictedOmissionCount}
              />
            ) : (
              "Not reported"
            )}
          </Fact>
          <Fact label="Events recorded">
            {view.summary?.eventCount !== null &&
            view.summary?.eventCount !== undefined ? (
              <span dir="ltr">{view.summary.eventCount}</span>
            ) : (
              "Not reported"
            )}
          </Fact>
        </dl>

        {view.summary && view.summary.completeness.missingRanges.length > 0 ? (
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Declared gaps</p>
            <ul className="mt-1 list-disc ps-5">
              {view.summary.completeness.missingRanges.map((range, index) => (
                <li key={index}>
                  Events{" "}
                  <span dir="ltr">
                    {range.fromSequence ?? "?"}–{range.toSequence ?? "?"}
                  </span>
                  {range.reason
                    ? `: ${OMISSION_REASON_LABELS[range.reason]}`
                    : ": reason not reported"}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {pollingSentence}
        </p>
      </CardBody>
    </Card>
  );
}

/** "5 seconds", or the raw millisecond figure when it is not a whole second. */
function describeInterval(ms: number): string {
  if (ms >= 1000 && ms % 1000 === 0) {
    const seconds = ms / 1000;
    return `${seconds} second${seconds === 1 ? "" : "s"}`;
  }
  return `${ms} ms`;
}

function CompletenessSentence({
  completeThroughSequence,
  lastSequence,
  missingRangeCount,
  shortfallOmissionCount,
  restrictedOmissionCount,
}: {
  completeThroughSequence: number | null;
  lastSequence: number | null;
  missingRangeCount: number;
  shortfallOmissionCount: number;
  restrictedOmissionCount: number;
}) {
  const parts: string[] = [];
  if (completeThroughSequence !== null && lastSequence !== null) {
    parts.push(
      `Complete through event ${completeThroughSequence} of ${lastSequence}`,
    );
  } else {
    parts.push("Sequence not reported");
  }
  if (missingRangeCount > 0) {
    parts.push(
      `${missingRangeCount} declared gap${missingRangeCount === 1 ? "" : "s"}`,
    );
  }
  if (shortfallOmissionCount > 0) {
    parts.push(
      `${shortfallOmissionCount} withheld field${shortfallOmissionCount === 1 ? "" : "s"}`,
    );
  }
  if (restrictedOmissionCount > 0) {
    parts.push(`${restrictedOmissionCount} restricted by policy (not a gap)`);
  }
  return <>{parts.join(" · ")}</>;
}

/**
 * Runtime and candidate evidence, displayed read-only (approved design §7.1:
 * "Identity fields are runtime evidence, not editable controls").
 *
 * TWO SOURCES, KEPT APART ON THE CARD. The service's own dispatch record says
 * which testing version was dispatched and under which target revision — facts
 * resolved server-side from the correlation row, and the only way a version is
 * ever named here (a content hash is shared by an unchanged refreeze, so it
 * cannot name one). The producer's runtime identity says what actually served:
 * every value is the producer's own string, a hex identity shortened the way
 * every other digest on a Project surface is (`shortHash`) with the full value
 * on the element's title, `unknown` SAID to be unattested rather than shown as
 * a coordinate, an absent axis read as not reported. All of it `dir="ltr"` — a
 * machine identifier keeps its own order inside a right-to-left page.
 */
function RunEvidenceCard({
  dispatch,
  currentTargetRevision,
  identity,
}: {
  dispatch: PreviewRunDispatch;
  /** The Project's current target revision, when the overview has loaded. */
  currentTargetRevision: number | null;
  identity: RunIdentity | null;
}) {
  const version = candidateDisplayVersion(dispatch.candidateRevision);
  const hash = identity?.candidateContentHash ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Runtime and candidate evidence</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <Fact label="Candidate dispatched">
            {version ?? (
              <>
                Test version not resolved
                <span className="block text-xs text-muted-foreground">
                  The candidate this run was dispatched with is no longer listed
                  for this project, so no version is named.
                </span>
              </>
            )}
            {dispatch.candidateRevision ? (
              <span className="block text-xs text-muted-foreground">
                Frozen{" "}
                <time dateTime={dispatch.candidateRevision.frozenAt} dir="ltr">
                  {formatDateTime(dispatch.candidateRevision.frozenAt)}
                </time>
              </span>
            ) : null}
          </Fact>
          <Fact label="Preview target revision">
            {describeTargetRevision(
              dispatch.previewTargetRevision,
              currentTargetRevision,
            )}
          </Fact>
        </dl>

        {identity === null ? (
          <p className="text-sm text-muted-foreground">
            No runtime identity was reported for this run. The serving Brain
            attests it once a trace has been captured.
          </p>
        ) : (
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <Fact label="Candidate content hash (attested)">
              {isAttested(hash) ? (
                <Identifier value={hash} />
              ) : hash === null ? (
                "Not reported"
              ) : (
                "Not attested by the serving Brain"
              )}
            </Fact>
            {IDENTITY_AXES.filter(
              (axis) => axis !== "candidateContentHash",
            ).map((axis) => {
              const value = identity[axis];
              return (
                <Fact key={axis} label={IDENTITY_LABELS[axis]}>
                  {isAttested(value) ? (
                    <Identifier value={value} />
                  ) : value === null ? (
                    "Not reported"
                  ) : (
                    "Not attested by the serving Brain"
                  )}
                </Fact>
              );
            })}
          </dl>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * The tab shell: a real ARIA tab list with a roving tab index and arrow-key
 * movement that follows the strip's rendered direction — in a right-to-left
 * page the next tab is to the LEFT — plus Home and End. The selected tab is
 * the ADDRESS's (`?tab=`), so an evidence link can open a tab and a saved
 * link reopens it; panels stay mounted and hidden rather than unmounted, so a
 * tab's loaded events and scroll survive a look at another tab. Prompt
 * Inspector reads nothing until first shown, then preserves its state too.
 */
function RunTabs({
  view,
  tab,
  onSelect,
  focusEventRef,
  projectId,
  previewRunRef,
  paused,
  pollIntervalMs,
}: {
  view: PreviewRunView;
  tab: RunTabId;
  onSelect: (tab: RunTabId) => void;
  /** The event the address asks the selected tab to open on, or null. */
  focusEventRef: string | null;
  projectId: string;
  previewRunRef: string;
  paused: boolean;
  pollIntervalMs: number;
}) {
  const tablistId = useId();
  // THE LATCHES ARE THIS RUN'S. They record which expensive panels the
  // clinician has opened, which is a fact about one run and not about the
  // route component React Router reuses for the next one. Reading the flags
  // only while their locator still agrees prevents a background list read or
  // a held comparison from crossing a parameter-only navigation.
  const [shownFor, setShownFor] = useState<{
    run: string;
    prompts: boolean;
    compare: boolean;
  }>({
    run: previewRunRef,
    prompts: tab === "prompts",
    compare: tab === "compare",
  });
  const promptsShown = shownFor.run === previewRunRef && shownFor.prompts;
  const compareShown = shownFor.run === previewRunRef && shownFor.compare;

  function markShown(panel: "prompts" | "compare") {
    setShownFor((current) => ({
      run: previewRunRef,
      prompts:
        panel === "prompts" ||
        (current.run === previewRunRef && current.prompts),
      compare:
        panel === "compare" ||
        (current.run === previewRunRef && current.compare),
    }));
  }

  function select(next: RunTabId) {
    // Latch both on entry and exit. Entry starts the locator read; exit keeps
    // its mounted panel and any explicitly loaded body alive while another
    // tab is shown. Compare latches for the same reason and one more: its
    // result is a mutation's, so unmounting the panel would throw away a
    // comparison the clinician asked for and make going back cost four more
    // whole-run reads on the recorded preview targets.
    if (tab === "prompts" || next === "prompts") {
      markShown("prompts");
    }
    if (tab === "compare" || next === "compare") {
      markShown("compare");
    }
    onSelect(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const index = RUN_TABS.indexOf(tab);
    let next: number;
    if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = RUN_TABS.length - 1;
    } else {
      const step = horizontalStep(
        event.key,
        closestDirection(event.currentTarget),
      );
      if (step === 0) {
        return;
      }
      next = (index + step + RUN_TABS.length) % RUN_TABS.length;
    }
    event.preventDefault();
    const target = RUN_TABS[next];
    if (target) {
      select(target);
      document.getElementById(`${tablistId}-${target}`)?.focus();
    }
  }


  return (
    <div>
      <div
        role="tablist"
        aria-label="Preview Insights sections"
        className="flex overflow-x-auto border-b border-border"
      >
        {RUN_TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`${tablistId}-${id}`}
            aria-selected={tab === id}
            aria-controls={`${tablistId}-${id}-panel`}
            tabIndex={tab === id ? 0 : -1}
            className={cn(
              "-mb-px shrink-0 border-b-2 px-3 py-2 text-sm whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              tab === id
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            onClick={() => select(id)}
            onKeyDown={onKeyDown}
          >
            {RUN_TAB_LABELS[id]}
          </button>
        ))}
      </div>

      {RUN_TABS.map((id) => (
        <div
          key={id}
          role="tabpanel"
          id={`${tablistId}-${id}-panel`}
          aria-labelledby={`${tablistId}-${id}`}
          hidden={tab !== id}
          tabIndex={0}
          className="pt-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {id === "prompts" ? (
            /*
              KEYED BY THE LOCATOR, exactly as the replay panel above is.
              React Router reuses this component across a parameter-only
              navigation — one run's page to another's — so without the key the
              inspector's own state would survive into a different run: its
              page cursor (asking run B for a position in run A), its
              selection, and the detail card's "already asked for" flag, which
              on two runs sharing an event ref would have fetched a body nobody
              asked for (#1130 review, finding 1).
            */
            <PreviewRunPromptInspector
              key={previewRunRef}
              projectId={projectId}
              previewRunRef={previewRunRef}
              view={view}
              shown={promptsShown || tab === "prompts"}
              paused={paused}
              pollIntervalMs={pollIntervalMs}
            />
          ) : id === "decisions" ? (
            <PreviewRunDecisions
              projectId={projectId}
              previewRunRef={previewRunRef}
              view={view}
              active={tab === "decisions"}
              paused={paused}
              pollIntervalMs={pollIntervalMs}
              focusEventRef={tab === "decisions" ? focusEventRef : null}
            />
          ) : id === "compare" ? (
            compareShown || tab === "compare" ? (
              <PreviewRunCompare
                key={previewRunRef}
                projectId={projectId}
                previewRunRef={previewRunRef}
              />
            ) : null
          ) : id === "conversation" ? (
            // KEYED BY THE LOCATOR for the reason the inspector above is: the
            // turn list, the "text requested" latch and each turn's detail read
            // are one run's, and must not survive a parameter-only navigation
            // into another run's page.
            <PreviewRunConversation
              key={previewRunRef}
              projectId={projectId}
              previewRunRef={previewRunRef}
              view={view}
              active={tab === "conversation"}
              paused={paused}
              pollIntervalMs={pollIntervalMs}
              focusEventRef={tab === "conversation" ? focusEventRef : null}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}
